export enum TextFormatType {
  Plain = "plain",
  Bold = "bold",
  Italic = "italic",
  Spoiler = "spoiler",
  Link = "link",
}

type TextFormatObject = {
  type: TextFormatType
  text: string
  data?: object
}

// Fucntion, that takes a string and returns an array of formatted objects
// If the text is in ** then it's bold
// If the text is in __ then it's italic
// If the text is in || then it's a spolier
// otherwise it's plain
export const formatText = (text: string): Array<TextFormatObject> => {
  const initial = prepareLinks(text)

  return processText(
    processText(
      processText(initial, "**", TextFormatType.Bold),
      "__",
      TextFormatType.Italic
    ),
    "||",
    TextFormatType.Spoiler
  )
}

const prepareLinks = (text: string): Array<TextFormatObject> => {
  const linkConfig = findUrls(text)
  linkConfig.sort((a, b) => a.start - b.start)

  const result: Array<TextFormatObject> = []
  let lastIndex = 0

  for (let conf of linkConfig) {
    if (conf.start > lastIndex) {
      result.push({
        type: TextFormatType.Plain,
        text: text.slice(lastIndex, conf.start),
      })
    }

    result.push({
      type: TextFormatType.Link,
      text: text.slice(conf.start, conf.start + conf.length),
      data: {
        url: conf.url,
      },
    })

    lastIndex = conf.start + conf.length
  }

  // We need to add the rest also
  if (lastIndex < text.length) {
    result.push({
      type: TextFormatType.Plain,
      text: text.slice(lastIndex),
    })
  }

  return result
}

const processText = (
  text: Array<TextFormatObject>,
  separator: string,
  type: TextFormatType
): Array<TextFormatObject> => {
  return text
    .map((t) => processTextPart(t, separator, type))
    .reduce((acc, el) => [...acc, ...el], [])
}

const processTextPart = (
  partlyFormated: TextFormatObject,
  separator: string,
  type: TextFormatType
): Array<TextFormatObject> => {
  if (partlyFormated.type === TextFormatType.Link) {
    return [partlyFormated]
  }

  const text = partlyFormated.text

  const escapedSeparator = escapeRegExp(separator)
  const r = new RegExp(`${escapedSeparator}(.*?)${escapedSeparator}`, "gs")

  const found = text.matchAll(r)

  const result: Array<TextFormatObject> = []
  let lastIndex = 0

  // @ts-ignore
  for (const entry of found) {
    if (entry == null || entry === undefined) {
      continue
    }

    const verbatim = entry[0] // enclosed in separator
    const raw = entry[1] // plain text that needs formatting

    const before = text.slice(lastIndex, entry.index)
    result.push({
      type: partlyFormated.type,
      text: before,
    })

    result.push({
      type: type,
      text: raw,
    })

    // @ts-ignore
    lastIndex = entry.index + verbatim.length
  }

  // We need to add the rest also
  result.push({
    type: partlyFormated.type,
    text: text.slice(lastIndex),
  })

  return result
}

function escapeRegExp(str: string) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") // $& means the whole matched string
}

export const getLanguageEmoji = (language: string): string | null => {
  switch (language) {
    case "en":
      return "🇬🇧"
    case "de":
      return "🇩🇪"
    case "fr":
      return "🇫🇷"
    case "ru":
      return "🇷🇺"
    case "es":
      return "🇪🇸"
    case "zh-cn":
      return "🇨🇳"
    case "zh-tw":
      return "🇹🇼"
    case "it":
      return "🇮🇹"
    case "uk":
      return "🇺🇦"
    case "pt":
      return "🇵🇹"
    case "nl":
      return "🇳🇱"
    case "el":
      return "🇬🇷"
    case "ar":
      return "🇦🇪"
    case "pl":
      return "🇵🇱"
    case "no":
      return "🇳🇴"
    case "fi":
      return "🇫🇮"
    case "da":
      return "🇩🇰"
    case "cs":
      return "🇨🇿"
    case "ro":
      return "🇷🇴"
    case "tr":
      return "🇹🇷"
    case "ja":
      return "🇯🇵"
  }
  return null
}

export type URLMatch = {
  url: string
  raw_match: string
  start: number
  length: number
}

const sanitizeUrl = (url: string): string => {
  if (!url.startsWith("http://") && !url.startsWith("https://")) {
    url = "http://" + url
  }

  return encodeURI(url)
}

export const findUrls = (messageText: string): URLMatch[] => {
  if (!messageText) {
    return []
  }

  const raw_regexp =
    /(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})/giu
  const regexp = new RegExp(raw_regexp)

  return Array.from(messageText.matchAll(regexp)).map((el) => ({
    url: sanitizeUrl(el[0]),
    raw_match: el[0],
    length: el[0].length,
    start: el.index || 0,
  }))
}

export const formatNumber = (n: number | null): string => {
  if (n == null) {
      return "—"
  }

  if (n < 1000) {
      return `${n}`
  } else {
      return `${Math.floor(n / 100) / 10}K`
  }
}
